import {
    AfterViewInit,
    Component,
    NgZone,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { NgForm, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription, timer } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';

import { AbstractCmpsForm, CmpsFormFields } from '../common/common.form';

import { TranslationKeyMap } from './auth.i18n';
import { AuthStatus, ResetPassword } from './auth.model';
import { AuthService } from './auth.service';

const ResetPasswordFormFields: CmpsFormFields = {
    NEW_PASSWORD: {
        id: 'newPassword',
        name: 'newPassword',
        label: TranslationKeyMap._692_NEW_PASSWORD,
        placeholder: TranslationKeyMap._692_NEW_PASSWORD,
        validators: [Validators.required, Validators.minLength(5)],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
            minlength:
                TranslationKeyMap._837_PASSWORD_MUST_BE_A_MINIMUM_OF__REQUIREDLENGTH__CHARACTERS_,
        },
    },
    NEW_PASSWORD_CONFIRM: {
        id: 'newPasswordConfirm',
        name: 'newPasswordConfirm',
        label: TranslationKeyMap._693_CONFIRM_NEW_PASSWORD,
        placeholder: TranslationKeyMap._693_CONFIRM_NEW_PASSWORD,
        // requiredWhenValidator, textEqualValidator
        validationMsgs: {
            requiredWhen: null,
            textEqual:
                TranslationKeyMap._653_PASSWORDS_DO_NOT_MATCH__PLEASE_TRY_AGAIN_,
        },
    },
};

class ResetPasswordForm extends AbstractCmpsForm {
    constructor(protected _translateService: TranslateService) {
        super(ResetPasswordFormFields, _translateService);
    }

    get name(): string {
        return 'resetPasswordForm';
    }
}

@Component({
    selector: 'cmps-auth-reset-password',
    templateUrl: './reset-password.auth.component.html',
})
export class ResetPasswordAuthComponent implements AfterViewInit, OnDestroy {
    @ViewChild('ngForm')
    ngForm: NgForm;

    i18n = TranslationKeyMap;
    hasPendingData: boolean;
    validToken: boolean;
    serverError: string;
    resetPasswordModel: ResetPassword;
    form: ResetPasswordForm;
    private _ngZoneSub: Subscription;
    private _sub: Subscription;
    success: boolean;
    failure: boolean;

    constructor(
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _ngZone: NgZone,
        private readonly _router: Router,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
    ) {}

    ngOnInit(): void {
        if (this._authService.authStatus.value === AuthStatus.AUTHENTICATED) {
            this.serverError =
                TranslationKeyMap._889_YOUR_PASSWORD_CANNOT_BE_RESET_AT_THIS_TIME__PLEASE_CONTACT_YOUR_COMPAS_ADMINISTRATOR_FOR_ASSISTANCE_;
            return;
        }

        const languageId = parseInt(this._translateService.currentLang);

        this.hasPendingData = true;
        this._activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
            if (!params.token) {
                this.serverError =
                    TranslationKeyMap._889_YOUR_PASSWORD_CANNOT_BE_RESET_AT_THIS_TIME__PLEASE_CONTACT_YOUR_COMPAS_ADMINISTRATOR_FOR_ASSISTANCE_;
                this.hasPendingData = false;
                return;
            }
            this._sub = this._authService
                .verifyResetPasswordToken(params.token, languageId)
                .pipe(
                    concatMap((langId) => {
                        const lang = langId.toString();
                        this._translateService.addLangs([lang]);
                        return this._translateService
                            .use(lang)
                            .pipe(catchError(() => of({})));
                    }),
                    map((ret) => {
                        this.validToken = true;
                        this.resetPasswordModel = {
                            tokenHash: params.token,
                            password: null,
                        };
                        this.form = new ResetPasswordForm(
                            this._translateService,
                        );
                        return true;
                    }),
                )
                .subscribe(
                    null,
                    (error) => {
                        this.hasPendingData = false;
                        this.serverError =
                            error.message ||
                            TranslationKeyMap._889_YOUR_PASSWORD_CANNOT_BE_RESET_AT_THIS_TIME__PLEASE_CONTACT_YOUR_COMPAS_ADMINISTRATOR_FOR_ASSISTANCE_;
                    },
                    // eslint-disable-next-line no-return-assign
                    () => (this.hasPendingData = false),
                );
        });
    }

    ngAfterViewInit(): void {
        this._ngZoneSub = this._ngZone.onStable.subscribe(() => {
            if (this.hasPendingData) {
                return;
            }
            if (this.ngForm) {
                this.form.form = this.ngForm;
                try {
                    this.form.bind();
                } catch (err) {
                    console.error(err);
                }
            }
            this._ngZoneSub.unsubscribe();
        });
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
        if (this._ngZoneSub) {
            this._ngZoneSub.unsubscribe();
        }
    }

    onSubmit(): void {
        this.form.validate();
        if (!this.form.isValid()) {
            return;
        }

        this.resetPasswordModel.languageId = parseInt(
            this._translateService.currentLang,
        );

        this.failure = false;
        this.form.submitted = true;
        this._sub = this._authService
            .resetPassword(this.resetPasswordModel)
            .subscribe(
                (ret) => {
                    this.success = true;
                    this.form.unbind();

                    // TODO revise
                    timer(10000)
                        // eslint-disable-next-line n/handle-callback-err, @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async
                        .subscribe(
                            null,
                            // eslint-disable-next-line n/handle-callback-err
                            (error) => null,
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async
                            () => this._router.navigate(['login']),
                        );
                },
                (error) => {
                    this.failure = true;
                    this.form.serverError =
                        error.message || TranslationKeyMap._704_UNKNOWN_ERROR;
                    this.form.submitted = false;
                },
                // eslint-disable-next-line no-return-assign
                () => (this.form.submitted = false),
            );
    }
}
